import { useState, KeyboardEvent, forwardRef } from 'react'
import { useTranslation } from 'react-i18next'
import { ArrowRight } from 'lucide-react'
import { cn } from '@/lib/utils'

interface ChatInputProps {
  onSend: (message: string) => void
  disabled?: boolean
}

export const ChatInput = forwardRef<HTMLInputElement, ChatInputProps>(
  function ChatInput({ onSend, disabled }, ref) {
    const { t } = useTranslation('common')
    const [input, setInput] = useState('')

    const handleSend = () => {
      if (input.trim() && !disabled) {
        onSend(input)
        setInput('')
      }
    }

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault()
        handleSend()
      }
    }

    const canSend = input.trim().length > 0 && !disabled

    return (
      <div
        className="px-4 py-3.5"
        style={{
          background: 'var(--color-surface)',
          borderTop: '1px solid var(--color-border)',
        }}
      >
        <div
          className={cn(
            'flex items-stretch gap-0',
            'border transition-colors duration-[var(--duration-fast)]',
            'focus-within:border-[var(--color-accent)]'
          )}
          style={{
            borderRadius: 'var(--radius-sm)',
            borderColor: 'var(--color-border)',
            background: 'var(--color-bg)',
          }}
        >
          <input
            ref={ref}
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t('chatbot.placeholder')}
            disabled={disabled}
            className={cn(
              'flex-1 min-w-0 px-3.5 py-2.5 text-[0.875rem] bg-transparent',
              'focus:outline-none',
              'disabled:opacity-50 disabled:cursor-not-allowed',
              'placeholder:text-[var(--color-muted)]'
            )}
            style={{ fontFamily: 'var(--font-body)' }}
          />
          <button
            type="button"
            onClick={handleSend}
            disabled={!canSend}
            className={cn(
              'flex-shrink-0 inline-flex items-center justify-center',
              'h-auto px-3.5 my-1 mr-1',
              'transition-[background,color,transform] duration-[var(--duration-fast)]',
              canSend
                ? 'bg-[var(--color-accent)] text-[var(--color-on-accent)] hover:bg-[var(--color-fg)]'
                : 'bg-transparent text-[var(--color-muted)] cursor-not-allowed'
            )}
            style={{ borderRadius: 'var(--radius-sm)' }}
            aria-label={t('chatbot.send')}
          >
            <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </div>

        {/* Footer micro-line — discreet brand legitimization */}
        <p
          className="mt-2.5 text-center"
          style={{
            fontFamily: 'var(--font-body)',
            fontSize: '0.625rem',
            letterSpacing: '0.18em',
            textTransform: 'uppercase',
            color: 'var(--color-muted)',
            opacity: 0.7,
          }}
        >
          <span aria-hidden="true" className="inline-block align-middle mr-2"
            style={{ width: '14px', height: '1px', background: 'var(--color-highlight)' }} />
          HSA Cosmetics
          <span aria-hidden="true" className="inline-block align-middle ml-2"
            style={{ width: '14px', height: '1px', background: 'var(--color-highlight)' }} />
        </p>
      </div>
    )
  }
)
